import { Box, FormControlLabel, IconButton, Switch, Typography } from '@mui/material'
import theme from '../theme'
import MenuIcon from '@mui/icons-material/Menu'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Context from '../Context'
import Person from '../components/Person'
import useInterval from '../utils/useInterval'

function AppBar() {
  const [ updateText, setUpdateText ] = useState('')
  const navigate = useNavigate()
  const { isSideBarOpen, setIsSideBarOpen, usPrices, setUsPrices, showDiscrepancies, setShowDiscrepancies, lastResponseTime, requestCount, setRequestCount, selectedLeague, selectedEvent, setSelectedEvent, showArbs, setShowArbs, showSettings, showNotificationSettings, activePage, currentLeague } = useContext(Context)

  useInterval(() => {
    /**
     * The staleness check below flips the figure to the fallen one after ten quiet seconds. On a screen
     * that polls nothing those ten seconds always arrive, and the app announces its own death while
     * working perfectly. Nothing to watch means nothing to judge.
     */
    if (showSettings || showNotificationSettings || activePage === 'support') {
      return
    }

    if (lastResponseTime) {
      const now = Date.now()
      let elapsedSeconds = Math.round((now - lastResponseTime) / 1000)

      if (elapsedSeconds === 0) {
        elapsedSeconds = 1
      }

      if (elapsedSeconds >= 10) {
        setRequestCount(-1)
      }

      /* if (elapsedSeconds > 2) {
        console.log('delay!')
      } */

      if (requestCount % 2 === 0 && elapsedSeconds === 2) {
        elapsedSeconds = 1
      }

      if (requestCount % 5 === 0 && elapsedSeconds === 1) {
        elapsedSeconds = 0.5
      }

      let timeText

      if (elapsedSeconds < 60) {
        timeText = `Last update: ${elapsedSeconds} sec ago`
      } else {
        const elapsedMinutes = Math.floor(elapsedSeconds / 60)
        timeText = `Last update: ${elapsedMinutes} min ago`
      }

      setUpdateText(timeText)
    } else {
      setUpdateText('')
    }
  }, 1000)

  const handleUsPricesChange = () => {
    setUsPrices(!usPrices)
  }

  const handleShowDiscrepanciesChange = () => {
    setShowDiscrepancies(!showDiscrepancies)
  }

  /**
   * The controls in this bar belong to particular screens, not to the app. Show Value Bets and Show Arbs
   * only mean something where rows are being shaded, and US Prices only where prices are on screen — on
   * the settings and notification screens all three are decoration that invites a pointless click.
   * Inside the Arbs or Value Bets sections the highlight is always on, so the switch is redundant there.
   */
  const onGrid = activePage === 'odds' && !selectedEvent

  /**
   * Only a real league earns the middle of the bar. The games board and the favourites board are their
   * own screens, Favorites says so in the menu already, and an event carries its own heading.
   */
  const named = onGrid && selectedLeague && selectedLeague !== 'Favorites'
  const title = showSettings ? 'Settings' : (named ? selectedLeague : '')
  const showingPrices = activePage === 'odds' || activePage === 'arb' || activePage === 'search'
    || activePage === 'board'
  const inOwnSection = currentLeague.group === 'Discrepancies' || currentLeague.group === 'Arbs'
  const showHighlightToggles = onGrid && !inOwnSection

  return(
    <Box sx={{ flexGrow: 1, flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', backgroundColor: theme.palette.primary.main, padding: '10px' }}>
        {!isSideBarOpen && (
          <IconButton onClick={() => { setIsSideBarOpen(true) }} sx={{ color: 'white', marginRight: '10px' }}>
            <MenuIcon />
          </IconButton>
        )}
        {/* Back means back to where you came from — the league you were reading — not to the root. It
            used to clear the event, which always landed on the front page whatever route led here. On a
            link opened cold there is nothing behind it, so then it falls back to clearing. */}
        {selectedEvent && (
          <IconButton
            onClick={() => {
              // idx counts entries this app pushed, unlike history.length which counts everything the
              // tab has ever shown — a shared link opened over another page used to walk out of the app
              const pushedByUs = window.history.state && window.history.state.idx > 0
              if (pushedByUs) {
                navigate(-1)
              } else {
                setSelectedEvent(null)
              }
            }}
            sx={{ color: 'white', marginRight: '10px' }}
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box sx={{ flexGrow: 1, justifyContent: 'center' }}>
          {/* The name of the league being read, and nothing else. Inside a single event it is already in
              the breadcrumb above the table; on the boards and on Favorites it was whatever the grid had
              selected last, which left "Favorites" standing over screens that are not it. */}
          <Typography variant="h3" align="center" sx={{ color: 'white' }}>
            {title}
          </Typography>
        </Box>
        {/* the toggles only appear where they change something: inside their own section the highlight
            is always on, and the settings screen and the single event view do not use them at all */}
        {showHighlightToggles && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={showDiscrepancies}
                  onChange={handleShowDiscrepanciesChange}
                  color="secondary"
                  sx={{
                    '& .MuiSwitch-track': {
                      backgroundColor: 'white'
                    },
                  }}
                />
              }
              label="Show Value Bets"
              sx={{ color: theme.palette.primary.contrastText }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showArbs}
                  onChange={() => setShowArbs(!showArbs)}
                  color="secondary"
                  sx={{
                    '& .MuiSwitch-track': {
                      backgroundColor: 'white'
                    },
                  }}
                />
              }
              label="Show Arbs"
              sx={{ color: theme.palette.primary.contrastText }}
            />
          </>
        )}
        {/* prices are only on screen on the grid, the event view and a single arb */}
        {showingPrices && (
        <FormControlLabel
          control={
            <Switch
              checked={usPrices}
              onChange={handleUsPricesChange}
              color="secondary"
              sx={{
                '& .MuiSwitch-track': {
                  backgroundColor: 'white'
                },
              }}
            />
          }
          label="US Prices"
          sx={{ color: theme.palette.primary.contrastText, marginRight: '30px' }}
        />
        )}
        {/* Settings pages poll nothing, so an ageing "last update" there reads as the app having died
            when in fact there is simply nothing to refresh. The figure stays either way — it is the mark
            of the app being alive, and taking it away is its own kind of alarm. */}
        <Box sx={{ width: '150px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          {!(showSettings || showNotificationSettings) && (
            <Typography sx={{ color: requestCount === -1 ? theme.palette.secondary.main : theme.palette.primary.contrastText, marginRight: '10px', fontSize: '12px' }}>{updateText}</Typography>
          )}
          <Person />
        </Box>
      </Box>
    </Box>
  )
}

export default AppBar